/**
 * TroiaComposeFloat - Compose flutuante nativo da plataforma
 * Reskin visual para o Design System (2026-07): chrome flat (sem gradiente),
 * tokens var(--theme-*), primário roxo var(--theme-button-primary).
 * Núcleo (máquina de estado, envio, upload, EmailComposer) intocado.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { Input, Select } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { EmailComposer } from '../../shared/components/EmailComposer';
import type { ComposeEmailData, ComposeState, UploadedAttachment } from '../../shared/types/email.types';
import { Icon } from '@/shared/icons'
interface TroiaComposeFloatProps {
  state: ComposeState;
  data: ComposeEmailData;
  showCc: boolean;
  showBcc: boolean;
  onSetShowCc: (show: boolean) => void;
  onSetShowBcc: (show: boolean) => void;
  onClose: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
  onRestore: () => void;
  onUpdateField: <K extends keyof ComposeEmailData>(field: K, value: ComposeEmailData[K]) => void;
  onSend: () => void;
  isSending: boolean;
  emailChannels?: Array<{ id: string; name: string; identifier: string }>;
  uploadedAttachments?: UploadedAttachment[];
  isUploading?: boolean;
  onRemoveAttachment?: (index: number) => void;
  onImageUpload?: (files: FileList) => void;
  onFileAttach?: (files: FileList) => void;
  hasContent?: boolean;
}

export const TroiaComposeFloat: React.FC<TroiaComposeFloatProps> = ({
  state,
  data,
  showCc,
  showBcc,
  onSetShowCc,
  onSetShowBcc,
  onClose,
  onMinimize,
  onMaximize,
  onRestore,
  onUpdateField,
  onSend,
  isSending,
  emailChannels,
  uploadedAttachments,
  isUploading,
  onRemoveAttachment,
  onImageUpload,
  onFileAttach,
  hasContent,
}) => {
  const confirm = useConfirm();
  // Corte em 899px, o mesmo do shell e do painel de leitura desta tela.
  const isMobile = useIsMobile(899);

  // Confirmação de descarte pelo primitivo do DS. O diálogo à mão que existia
  // aqui tinha `w-[360px]` fixo e `z-[9998]/[9999]`, fora da escala de overlays.
  const handleClose = async (): Promise<void> => {
    if (!hasContent) {
      onClose();
      return;
    }
    const ok = await confirm({
      title: 'Descartar rascunho?',
      message: 'Seu e-mail não salvo será perdido.',
      confirmButtonText: 'Descartar',
      cancelButtonText: 'Continuar editando',
      variant: 'danger',
    });
    if (ok) onClose();
  };

  if (state === 'closed') return null;

  const isMinimized = state === 'minimized';
  // No celular a janela é SEMPRE tela cheia: 520px de largura ancorados em
  // `right: 24px` deixavam ~154px do compose fora da tela num aparelho de
  // 390px — justamente onde ficam os campos e o botão Enviar.
  const isFullscreen = state === 'fullscreen' || isMobile;

  // Label das linhas de campo (De / Para / Cc / Cco / Assunto)
  const fieldLabelStyle: React.CSSProperties = {
    minWidth: 56,
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--theme-muted-text)',
  };

  // Campos do cabeçalho do compose usam os primitivos shared (NUNCA #9/#32 —
  // nativo `<input>`/`<select>` é proibido em overlay), mas mantendo a linha
  // inline sem moldura do desenho original: o primitivo entra com borda e fundo
  // zerados. Altura 44px no celular (alvo de toque), 38 no desktop.
  const inlineFieldClass = 'flex-1 min-w-0 !border-0 !bg-transparent !px-0 !shadow-none h-11 min-[900px]:h-[38px]';
  const inlineFieldStyle: React.CSSProperties = { color: 'var(--theme-card-text)', fontSize: 14 };

  if (isMinimized) {
    return createPortal(
      <div
        data-testid="troia-compose-minimized"
        className="fixed bottom-0 right-6 z-50 cursor-pointer flex items-center justify-between gap-2 px-4 py-2.5"
        style={{
          backgroundColor: 'var(--theme-button-primary)',
          color: '#ffffff',
          width: 280,
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 -1px 0 rgba(255,255,255,0.10) inset, 0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={onRestore}
      >
        <span className="font-medium truncate" style={{ fontSize: 13 }}>
          {data.subject || 'Novo Email'}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onRestore(); }}
            className="flex items-center justify-center transition-colors"
            style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.08)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.20)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
          >
            <Icon name="maximize" className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); void handleClose(); }}
            className="flex items-center justify-center transition-colors"
            style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.08)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.20)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
          >
            <Icon name="x" className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>,
      document.body
    );
  }

  const modalContent = (
    <div
      data-testid="troia-compose-float"
      className={`fixed z-[60] flex flex-col overflow-hidden ${
        isMobile ? 'inset-0' : isFullscreen ? 'inset-4' : 'bottom-0 right-6'
      }`}
      style={{
        width: isFullscreen ? undefined : 520,
        height: isFullscreen ? undefined : 520,
        borderRadius: isMobile ? 0 : isFullscreen ? 12 : '12px 12px 0 0',
        // Janela flutua sobre o inbox (desktop e mobile) — tint sobre base opaca
        background: 'var(--theme-overlay-card-background)',
        border: '1px solid var(--theme-card-border)',
        boxShadow: '0 12px 48px rgba(0,0,0,0.18), 0 0 1px rgba(0,0,0,0.10)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{
          backgroundColor: 'var(--theme-button-primary)',
          color: '#ffffff',
        }}
      >
        <span className="font-semibold" style={{ fontSize: 14, letterSpacing: '-0.01em' }}>Novo Email</span>
        <div className="flex items-center gap-1">
          <button
            onClick={onMinimize}
            className="flex items-center justify-center transition-colors"
            style={{
              width: isMobile ? 44 : 30,
              height: isMobile ? 44 : 30,
              borderRadius: 7,
              backgroundColor: 'rgba(255,255,255,0.10)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.20)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
            data-testid="troia-compose-minimize"
            aria-label="Minimizar"
          >
            <Icon name="minus" className="h-4 w-4" />
          </button>
          <button
            onClick={isFullscreen ? onRestore : onMaximize}
            className="flex items-center justify-center transition-colors"
            style={{
              width: isMobile ? 44 : 30,
              height: isMobile ? 44 : 30,
              borderRadius: 7,
              backgroundColor: 'rgba(255,255,255,0.10)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.20)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
            data-testid="troia-compose-maximize"
            aria-label={isFullscreen ? 'Restaurar' : 'Maximizar'}
          >
            {isFullscreen ? <Icon name="minimize" className="h-4 w-4" /> : <Icon name="maximize" className="h-4 w-4" />}
          </button>
          <button
            onClick={() => { void handleClose(); }}
            className="flex items-center justify-center transition-colors"
            style={{
              width: isMobile ? 44 : 30,
              height: isMobile ? 44 : 30,
              borderRadius: 7,
              backgroundColor: 'rgba(255,255,255,0.10)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.20)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)'; }}
            data-testid="troia-compose-close"
            aria-label="Fechar"
          >
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Fields */}
      <div className="px-4 py-1 flex-shrink-0" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
        {/* From */}
        {emailChannels && emailChannels.length > 0 && (
          <div
            className="flex items-center gap-2 py-2 border-b"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <span style={fieldLabelStyle}>De</span>
            <Select
              value={data.channelId || ''}
              onChange={(value) => onUpdateField('channelId', value)}
              placeholder="Selecione o canal..."
              options={emailChannels.map((ch) => ({ value: ch.id, label: `${ch.name} (${ch.identifier})` }))}
              className={`${inlineFieldClass} cursor-pointer`}
              style={inlineFieldStyle}
              data-testid="troia-compose-from"
            />
          </div>
        )}

        {/* To */}
        <div
          className="flex items-center gap-2 py-2 border-b"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <span style={fieldLabelStyle}>Para</span>
          <Input
            type="text"
            value={data.to.join(', ')}
            onChange={(e) => onUpdateField('to', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            className={inlineFieldClass}
            style={inlineFieldStyle}
            data-testid="troia-compose-to"
          />
          <div className="flex items-center gap-2" style={{ fontSize: 13 }}>
            {!showCc && (
              <button
                onClick={() => onSetShowCc(true)}
                style={{ color: 'var(--theme-muted-text)', fontWeight: 500 }}
                className="hover:underline"
              >
                Cc
              </button>
            )}
            {!showBcc && (
              <button
                onClick={() => onSetShowBcc(true)}
                style={{ color: 'var(--theme-muted-text)', fontWeight: 500 }}
                className="hover:underline"
              >
                Cco
              </button>
            )}
          </div>
        </div>

        {/* Cc */}
        {showCc && (
          <div
            className="flex items-center gap-2 py-2 border-b"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <span style={fieldLabelStyle}>Cc</span>
            <Input
              type="text"
              value={(data.cc || []).join(', ')}
              onChange={(e) => onUpdateField('cc', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              className={inlineFieldClass}
              style={inlineFieldStyle}
              data-testid="troia-compose-cc"
            />
          </div>
        )}

        {/* Bcc */}
        {showBcc && (
          <div
            className="flex items-center gap-2 py-2 border-b"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <span style={fieldLabelStyle}>Cco</span>
            <Input
              type="text"
              value={(data.bcc || []).join(', ')}
              onChange={(e) => onUpdateField('bcc', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              className={inlineFieldClass}
              style={inlineFieldStyle}
              data-testid="troia-compose-bcc"
            />
          </div>
        )}

        {/* Subject */}
        <div className="flex items-center gap-2 py-2">
          <span style={fieldLabelStyle}>Assunto</span>
          <Input
            type="text"
            value={data.subject}
            onChange={(e) => onUpdateField('subject', e.target.value)}
            className={inlineFieldClass}
            style={inlineFieldStyle}
            data-testid="troia-compose-subject"
          />
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <EmailComposer
          value={data.body}
          onChange={(html) => onUpdateField('body', html)}
          minHeight={120}
          onImageUpload={onImageUpload}
          onFileAttach={onFileAttach}
        />
      </div>

      {/* Uploaded Attachments */}
      {uploadedAttachments && uploadedAttachments.length > 0 && (
        <div
          className="flex items-center gap-2 px-4 py-2.5 overflow-x-auto flex-shrink-0 border-t"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          {uploadedAttachments.map((att, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border flex-shrink-0"
              style={{
                fontSize: 12,
                fontWeight: 500,
                borderColor: 'var(--theme-card-border)',
                backgroundColor: 'var(--theme-page-background)',
                color: 'var(--theme-card-text)',
              }}
            >
              <Icon name="paperclip" className="h-3 w-3 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
              <span className="truncate max-w-[120px]">{att.filename}</span>
              <button
                onClick={() => onRemoveAttachment?.(i)}
                className="flex items-center justify-center rounded-full transition-colors"
                style={{ width: 18, height: 18, color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon name="x" className="h-3 w-3" />
              </button>
            </div>
          ))}
          {isUploading && (
            <div className="flex items-center gap-1.5 flex-shrink-0" style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>
              <Icon name="loader" className="h-3 w-3 animate-spin" />
              Enviando...
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        className="flex items-center justify-between px-4 py-3 border-t flex-shrink-0 relative z-10"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <button
          onClick={onSend}
          disabled={isSending || !data.to.length || !data.channelId}
          className="flex items-center gap-2 px-5 rounded-lg font-semibold transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          style={{
            height: 38,
            fontSize: 14,
            backgroundColor: 'var(--theme-button-primary)',
            color: '#ffffff',
          }}
          onMouseEnter={(e) => {
            if (!isSending) e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)';
          }}
        >
          <Icon name="send" className="h-4 w-4" />
          {isSending ? 'Enviando...' : 'Enviar'}
        </button>
        <button
          onClick={() => { void handleClose(); }}
          className="flex items-center justify-center rounded-lg transition-colors"
          style={{ width: isMobile ? 44 : 38, height: isMobile ? 44 : 38, color: 'var(--theme-muted-text)' }}
          data-testid="troia-compose-discard"
          aria-label="Descartar"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Icon name="trash" className="h-4 w-4" />
        </button>
      </div>

    </div>
  );

  return createPortal(modalContent, document.body);
};

TroiaComposeFloat.displayName = 'TroiaComposeFloat';
